import { useMemo } from 'react';
import { useAppSelector } from '../../../../app/hooks';
import { useVarietiesByCustomer } from '../../../../services/entities/hooks/useVarietiesByCustomer';
import {
  FilteredOnboardApiData,
  OnboardState,
} from '../../context/OnboardContext';
import {
  selectFilterCropType,
  selectFilterCustomerID,
  selectFilterRanchID,
} from '../../onboardingSlice';
import { useGetOnboardingData } from '../useGetOnboardingData';
import { filterBlocks } from './utils/filterBlocks';
import { filterCustomers } from './utils/filterCustomers';
import { filterRanches } from './utils/filterRanches';
import { filterUsers } from './utils/filterUsers';

const useFilterOnboardApiData = () => {
  const filterCustomerID = useAppSelector(selectFilterCustomerID);
  const filterRanchID = useAppSelector(selectFilterRanchID);
  const filterCropType = useAppSelector(selectFilterCropType);

  // Fetch data from API
  const onboardApiDataRaw = useGetOnboardingData();

  // Get varieties for the filtered customer to support crop type filtering
  const { varieties } = useVarietiesByCustomer(filterCustomerID);

  const filteredCustomers = useMemo(
    () => filterCustomers(onboardApiDataRaw.customers, filterCustomerID),
    [onboardApiDataRaw.customers, filterCustomerID]
  );

  const filteredRanches = useMemo(
    () => filterRanches(onboardApiDataRaw.ranches, filterCustomerID),
    [onboardApiDataRaw.ranches, filterCustomerID]
  );

  const filteredBlocks = useMemo(
    () =>
      filterBlocks(
        onboardApiDataRaw.blocks,
        filterCustomerID,
        filterRanchID,
        filteredRanches,
        filterCropType,
        varieties
      ),
    [
      onboardApiDataRaw.blocks,
      filterCustomerID,
      filterRanchID,
      filteredRanches,
      filterCropType,
      varieties,
    ]
  );

  const filteredUsers = useMemo(
    () => filterUsers(onboardApiDataRaw.users, filterCustomerID),
    [onboardApiDataRaw.users, filterCustomerID]
  );

  const filteredOnboardApiData: FilteredOnboardApiData = {
    customers: filteredCustomers,
    ranches: filteredRanches,
    blocks: filteredBlocks,
    users: filteredUsers,
  };

  const result: OnboardState = {
    ApiDataRaw: onboardApiDataRaw,
    ApiDataFiltered: filteredOnboardApiData,
  };

  return result;
};

export default useFilterOnboardApiData;
